import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {
  APIResponse,
  GetHospitalPocListRequestPayload,
  HospitalAdminSliceInterface,
  GetHospitalPocListSuccessResponse,
  UpdateHospitalPocStatusRequestPayload,
  UpdateHospitalPocStatusSuccessResponse,
  AddHospitalPocSuccessResponse,
  AddHospitalPocRequestPayload,
  HospitalPocProfileSuccessResponse,
  UpdateHospitalProfileSuccessResponse,
  GetHospitalSpecialistsSuccessResponse,
  GetHospitalSpecialistsRequestPayload,
  InviteHospitalSpecialistRequestPayload,
  InviteHospitalSpecialistSuccessResponse,
  EditHospitalSpecialistRequestPayload,
  EditHospitalSpecialistSuccessResponse,
  GetHospitalConsultationCaseListRequestPayload,
  GetHospitalConsultationCaseListSuccessResponse,
  AssignHospitalSpecialistToConsultationRequestPayload,
  GetHospitalSpecialistListSuccessResponse,
  HospitalConsultationCase,
  SubmitHelpRequestRequestPayload,
  SubmitHelpRequestSuccessResponse,
} from '@/interface';

const initialState: HospitalAdminSliceInterface = {
  isLoading: false,
  profileEditLoading: false,
  hospitalAdminPocListLoading: false,
  specialistInviteLoading: false,

  hospitalAdminDashboardSuccess: false,
  hospitalAdminDashboardFailure: false,
  dashboard: {
    admins: [],
    current_page: 0,
    total_pages: 0,
    next_page: null,
    total_results: 0,
  },
  hospitalPocProfile: null,

  poc_dashboard: {
    specialists: [],
    current_page: 0,
    total_pages: 0,
    next_page: null,
    total_results: 0,
    no_data: false,
  },
  consultation_cases: {
    consultations: [],
    current_page: 0,
    total_pages: 0,
    next_page: null,
    total_results: 0,
  },
  hospital_specialists_all: [],
};

const hospitalAdminSlice = createSlice({
  name: 'hospitalAdmin',
  initialState,
  reducers: {
    /*
     * get hospital poc list
     */
    getHospitalPocListRequest: (state, action: PayloadAction<GetHospitalPocListRequestPayload>) => {
      state.isLoading = true;
      state.hospitalAdminDashboardSuccess = false;
      state.hospitalAdminDashboardFailure = false;
    },
    getHospitalPocListSuccess: (state, action: PayloadAction<GetHospitalPocListSuccessResponse>) => {
      state.isLoading = false;
      state.hospitalAdminDashboardSuccess = action.payload.status_code;
      state.dashboard.admins = action.payload.data.hospital_poc;
      state.dashboard.current_page = action.payload.data.current_page;
      state.dashboard.total_pages = action.payload.data.total_pages;
      state.dashboard.next_page = action.payload.data.next_page;
      state.dashboard.total_results = action.payload.data.total_results;
    },
    getHospitalPocListFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.hospitalAdminDashboardFailure = action.payload.status_code;
      if (['E-10054', 'E-10044'].includes(action.payload.status_code)) {
        state.dashboard.admins = [];
        state.dashboard.current_page = 0;
        state.dashboard.total_pages = 0;
        state.dashboard.next_page = null;
        state.dashboard.total_results = 0;
      }
    },

    /**
     * update hospital poc status
     */
    updateHospitalPocStatusRequest: (state, action: PayloadAction<UpdateHospitalPocStatusRequestPayload>) => {
      state.isLoading = true;
      state.hospitalAdminDashboardSuccess = false;
      state.hospitalAdminDashboardFailure = false;
    },
    updateHospitalPocStatusSuccess: (state, action: PayloadAction<UpdateHospitalPocStatusSuccessResponse>) => {
      state.isLoading = false;
      state.hospitalAdminDashboardSuccess = action.payload.status_code;
      state.dashboard.admins = state.dashboard.admins.map(admin =>
        admin.id === action.payload.data.id
          ? {
              ...admin,
              status: action.payload.data.status,
            }
          : admin,
      );
    },

    updateHospitalPocStatusFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.hospitalAdminDashboardFailure = action.payload.status_code;
    },

    /**
     * add hospital poc
     */
    addHospitalPocRequest: (state, action: PayloadAction<AddHospitalPocRequestPayload>) => {
      state.hospitalAdminPocListLoading = true;
      state.hospitalAdminDashboardSuccess = false;
      state.hospitalAdminDashboardFailure = false;
    },
    addHospitalPocSuccess: (state, action: PayloadAction<AddHospitalPocSuccessResponse>) => {
      state.hospitalAdminPocListLoading = false;
      state.hospitalAdminDashboardSuccess = action.payload.status_code;
    },
    addHospitalPocFailure: (state, action: PayloadAction<APIResponse>) => {
      state.hospitalAdminPocListLoading = false;
      state.hospitalAdminDashboardFailure = action.payload.status_code;
    },

    /**
     * get hospital profile
     */
    getHospitalProfileRequest: state => {
      state.isLoading = true;
      state.hospitalAdminDashboardSuccess = false;
      state.hospitalAdminDashboardFailure = false;
    },
    getHospitalProfileSuccess: (state, action: PayloadAction<HospitalPocProfileSuccessResponse>) => {
      state.isLoading = false;
      state.hospitalAdminDashboardSuccess = action.payload.status_code;
      state.hospitalPocProfile = action.payload.data;
    },
    getHospitalProfileFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.hospitalAdminDashboardFailure = action.payload.status_code;
    },

    /**
     * update hospital profile
     */
    updateHospitalProfileRequest: (state, action: PayloadAction<any>) => {
      // since formdata
      state.profileEditLoading = true;
      state.hospitalAdminDashboardSuccess = false;
      state.hospitalAdminDashboardFailure = false;
    },
    updateHospitalProfileSuccess: (state, action: PayloadAction<UpdateHospitalProfileSuccessResponse>) => {
      state.profileEditLoading = false;
      state.hospitalAdminDashboardSuccess = action.payload.status_code;
      state.hospitalPocProfile = action.payload.data;
    },
    updateHospitalProfileFailure: (state, action: PayloadAction<APIResponse>) => {
      state.profileEditLoading = false;
      state.hospitalAdminDashboardFailure = action.payload.status_code;
    },

    /**
     * get hospital specialists
     */
    getHospitalSpecialistsRequest: (state, action: PayloadAction<GetHospitalSpecialistsRequestPayload>) => {
      state.isLoading = true;
      state.hospitalAdminDashboardSuccess = false;
      state.hospitalAdminDashboardFailure = false;
      state.poc_dashboard.no_data = false;
    },

    getHospitalSpecialistsSuccess: (state, action: PayloadAction<GetHospitalSpecialistsSuccessResponse>) => {
      state.isLoading = false;
      state.hospitalAdminDashboardSuccess = action.payload.status_code;
      state.poc_dashboard.specialists = action.payload.data.specialists;
      state.poc_dashboard.current_page = action.payload.data.current_page;
      state.poc_dashboard.total_pages = action.payload.data.total_pages;
      state.poc_dashboard.next_page = action.payload.data.next_page;
      state.poc_dashboard.total_results = action.payload.data.total_results;
      state.poc_dashboard.no_data = false;
    },
    getHospitalSpecialistsFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.hospitalAdminDashboardFailure = action.payload.status_code;
      if (['E-10054', 'E-10044'].includes(action.payload.status_code)) {
        state.poc_dashboard.specialists = [];
        state.poc_dashboard.current_page = 0;
        state.poc_dashboard.total_pages = 0;
        state.poc_dashboard.next_page = null;
        state.poc_dashboard.total_results = 0;
        if (action.payload.status_code === 'E-10054') {
          state.poc_dashboard.no_data = true;
        }
      }
    },

    /**
     * invite hospital specialist
     */
    inviteHospitalSpecialistRequest: (state, action: PayloadAction<InviteHospitalSpecialistRequestPayload>) => {
      state.specialistInviteLoading = true;
      state.hospitalAdminDashboardSuccess = false;
      state.hospitalAdminDashboardFailure = false;
    },
    inviteHospitalSpecialistSuccess: (state, action: PayloadAction<InviteHospitalSpecialistSuccessResponse>) => {
      state.specialistInviteLoading = false;
      state.hospitalAdminDashboardSuccess = action.payload.status_code;
    },
    inviteHospitalSpecialistFailure: (state, action: PayloadAction<APIResponse>) => {
      state.specialistInviteLoading = false;
      state.hospitalAdminDashboardFailure = action.payload.status_code;
    },

    /**
     * edit hospital specialist
     */
    editHospitalSpecialistRequest: (state, action: PayloadAction<EditHospitalSpecialistRequestPayload>) => {
      state.specialistInviteLoading = true;
      state.hospitalAdminDashboardSuccess = false;
      state.hospitalAdminDashboardFailure = false;
    },
    editHospitalSpecialistSuccess: (state, action: PayloadAction<EditHospitalSpecialistSuccessResponse>) => {
      state.specialistInviteLoading = false;
      state.hospitalAdminDashboardSuccess = action.payload.status_code;
      // Update the specialist in the list
      state.poc_dashboard.specialists = state.poc_dashboard.specialists.map(specialist =>
        specialist.id === action.payload.data.id ? action.payload.data : specialist
      );
    },
    editHospitalSpecialistFailure: (state, action: PayloadAction<APIResponse>) => {
      state.specialistInviteLoading = false;
      state.hospitalAdminDashboardFailure = action.payload.status_code;
    },

    /**
     * get hospital consultation case list
     */
    getHospitalConsultationCaseListRequest: (state, action: PayloadAction<GetHospitalConsultationCaseListRequestPayload>) => {
      state.isLoading = true;
      state.hospitalAdminDashboardSuccess = false;
      state.hospitalAdminDashboardFailure = false;
    },
    getHospitalConsultationCaseListSuccess: (state, action: PayloadAction<GetHospitalConsultationCaseListSuccessResponse>) => {
      state.isLoading = false;
      state.hospitalAdminDashboardSuccess = action.payload.status_code;
      state.consultation_cases.consultations = action.payload.data.consultations;
      state.consultation_cases.current_page = action.payload.data.current_page;
      state.consultation_cases.total_pages = action.payload.data.total_pages;
      state.consultation_cases.next_page = action.payload.data.next_page;
      state.consultation_cases.total_results = action.payload.data.total_results;
    },
    getHospitalConsultationCaseListFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.hospitalAdminDashboardFailure = action.payload.status_code;
      if (['E-10054', 'E-10044'].includes(action.payload.status_code)) {
        state.consultation_cases.consultations = [];
        state.consultation_cases.current_page = 0;
        state.consultation_cases.total_pages = 0;
        state.consultation_cases.next_page = null;
        state.consultation_cases.total_results = 0;
      }
    },

    /**
     * assign hospital specialist to consultation case
     */
    assignHospitalSpecialistToConsultationRequest: (state, action: PayloadAction<AssignHospitalSpecialistToConsultationRequestPayload>) => {
      state.isLoading = true;
      state.hospitalAdminDashboardSuccess = false;
      state.hospitalAdminDashboardFailure = false;
    },
    assignHospitalSpecialistToConsultationSuccess: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.hospitalAdminDashboardSuccess = action.payload.status_code;
    },
    assignHospitalSpecialistToConsultationFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.hospitalAdminDashboardFailure = action.payload.status_code;
    },

    /**
     * get hospital specialist all list
     */
    getHospitalSpecialistAllRequest: state => {
      state.isLoading = true;
      state.hospitalAdminDashboardSuccess = false;
      state.hospitalAdminDashboardFailure = false;
    },
    getHospitalSpecialistAllSuccess: (state, action: PayloadAction<GetHospitalSpecialistListSuccessResponse>) => {
      state.isLoading = false;
      state.hospitalAdminDashboardSuccess = action.payload.status_code;
      state.hospital_specialists_all = action.payload.data.specialists;
    },
    getHospitalSpecialistAllFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.hospitalAdminDashboardFailure = action.payload.status_code;
    },

    // cancel and reset reducers
    cancelHospitalAdminRequest: state => {
      state.isLoading = false;
      state.hospitalAdminDashboardSuccess = false;
      state.hospitalAdminDashboardFailure = false;
      state.specialistInviteLoading = false;
      state.hospitalAdminPocListLoading = false;
    },
    resetHospitalAdminMessage: state => {
      state.hospitalAdminDashboardSuccess = false;
      state.hospitalAdminDashboardFailure = false;
    },

    /**
     * submit hospital help request
     */
    submitHospitalHelpRequestRequest: (state, action: PayloadAction<SubmitHelpRequestRequestPayload>) => {
      state.isLoading = true;
      state.hospitalAdminDashboardSuccess = false;
      state.hospitalAdminDashboardFailure = false;
    },
    submitHospitalHelpRequestSuccess: (state, action: PayloadAction<SubmitHelpRequestSuccessResponse>) => {
      state.isLoading = false;
      state.hospitalAdminDashboardSuccess = action.payload.status_code;
    },
    submitHospitalHelpRequestFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.hospitalAdminDashboardFailure = action.payload.status_code;
    },

    // utilty
    addHospitalConsultationCaseData(state, action: PayloadAction<HospitalConsultationCase>) {
      // do not add if consultation case already exists
      const alreadyExists = state.consultation_cases.consultations.some(c => c.id === action.payload.id);
      if (alreadyExists) return;

      const next = [{...action.payload}, ...state.consultation_cases.consultations];

      state.consultation_cases.consultations = next.slice(0, 10);
    },

    updateHospitalConsultationCaseData(state, action: PayloadAction<{id: number; data: Partial<Omit<HospitalConsultationCase, 'id'>>}>) {
      state.consultation_cases.consultations = state.consultation_cases.consultations.map(consultation => {
        if (consultation.id === action.payload.id) {
          return {...consultation, ...action.payload.data};
        }
        return consultation;
      });
    },

    /**
     * Update specialist invitation status (triggered by WebSocket when specialist registers)
     */
    updateSpecialistInvitationStatus(state, action: PayloadAction<{id: number; onboarding_status: {id: number; label: string}}>) {
      state.poc_dashboard.specialists = state.poc_dashboard.specialists.map(specialist => {
        if (specialist.id === action.payload.id) {
          return {...specialist, onboarding_status: action.payload.onboarding_status};
        }
        return specialist;
      });
    },
  },
});

export const {
  // get hospital poc list
  getHospitalPocListRequest,
  getHospitalPocListSuccess,
  getHospitalPocListFailure,

  // update hospital poc status
  updateHospitalPocStatusRequest,
  updateHospitalPocStatusSuccess,
  updateHospitalPocStatusFailure,

  // add hospital poc
  addHospitalPocRequest,
  addHospitalPocSuccess,
  addHospitalPocFailure,

  // get hospital profile
  getHospitalProfileRequest,
  getHospitalProfileSuccess,
  getHospitalProfileFailure,

  // update hospital profile
  updateHospitalProfileRequest,
  updateHospitalProfileSuccess,
  updateHospitalProfileFailure,

  // get hospital specialists
  getHospitalSpecialistsRequest,
  getHospitalSpecialistsSuccess,
  getHospitalSpecialistsFailure,

  // invite hospital specialist
  inviteHospitalSpecialistRequest,
  inviteHospitalSpecialistSuccess,
  inviteHospitalSpecialistFailure,

  // edit hospital specialist
  editHospitalSpecialistRequest,
  editHospitalSpecialistSuccess,
  editHospitalSpecialistFailure,

  // get hospital consultation case list
  getHospitalConsultationCaseListRequest,
  getHospitalConsultationCaseListSuccess,
  getHospitalConsultationCaseListFailure,

  // assign hospital specialist to consultation case
  assignHospitalSpecialistToConsultationRequest,
  assignHospitalSpecialistToConsultationSuccess,
  assignHospitalSpecialistToConsultationFailure,

  // get hospital specialist all list
  getHospitalSpecialistAllRequest,
  getHospitalSpecialistAllSuccess,
  getHospitalSpecialistAllFailure,

  // submit hospital help request
  submitHospitalHelpRequestRequest,
  submitHospitalHelpRequestSuccess,
  submitHospitalHelpRequestFailure,

  // cancel and reset reducers
  cancelHospitalAdminRequest,
  resetHospitalAdminMessage,

  // utility
  addHospitalConsultationCaseData,
  updateHospitalConsultationCaseData,
  updateSpecialistInvitationStatus,
} = hospitalAdminSlice.actions;

export default hospitalAdminSlice.reducer;
